// Confirmation for a MANUAL fulfilment — Orders page → select rows →
// Change status to → Fulfilled → Apply.
//
// Marking an order fulfilled by hand is not a reversible tidy-up: it
// consumes the stock reservation and tells the sales channel the order
// shipped (after that warehouse's release delay). Selecting a row by
// accident on a 250-row page is easy, so the action stops here for a
// confirmation naming how many orders it is about to close.
//
// The second job of this screen is the tracking details. These orders
// shipped OUTSIDE the pack desk — hand-carried, collected, or booked on
// a courier account Portal doesn't touch — so Portal has no label and
// therefore no carrier, code or link to pass on. Whatever the operator
// knows can be typed here, per order: it is stored as the order's
// shipment and travels to the sales channel with the fulfilment
// notification, exactly as a desk-recorded label would.
//
// Everything in the expander is OPTIONAL — the whole point is that a
// manual fulfil usually has nothing to record, and typing nothing must
// stay a single click away. The one exception is Amazon, which refuses
// a shipment confirmation without a tracking code; that shows up as a
// per-order note in the result panel afterwards rather than as a block
// here, because Portal doesn't know the channel of every selected row
// at this point.
import { useMemo, useState } from "react";
import { CheckCircle, ChevronDown, ChevronRight, X } from "lucide-react";
import { useTranslation } from "../../i18n";

// One selected order, narrowed to what this screen shows.
export type ManualFulfilTarget = {
    order_id: string;
    order_number: string;
};

// What the operator typed for one order. Empty strings mean "not filled
// in" — the caller drops them rather than sending blanks.
export type ManualTrackingEntry = {
    carrier: string;
    tracking_number: string;
    tracking_url: string;
};

type Props = {
    orders: ManualFulfilTarget[];
    busy: boolean;
    onCancel: () => void;
    // Keyed by order_id, and only for orders with something filled in.
    onConfirm: (tracking: Map<string, ManualTrackingEntry>) => void;
};

const EMPTY: ManualTrackingEntry = { carrier: "", tracking_number: "", tracking_url: "" };

// A tracking link is rendered as a clickable href both in Portal and —
// once forwarded — in the channel's own admin, so only http(s) is
// accepted. Checked here so a typo is caught before the fulfil rather
// than coming back as a row error with the orders already closed.
const linkIsUsable = (raw: string): boolean => {
    const s = raw.trim();
    if (s === "") return true;
    try {
        const u = new URL(s);
        return u.protocol === "http:" || u.protocol === "https:";
    } catch {
        return false;
    }
};

const hasAnything = (e: ManualTrackingEntry): boolean =>
    e.carrier.trim() !== "" || e.tracking_number.trim() !== "" || e.tracking_url.trim() !== "";

const ManualFulfilModal = ({ orders, busy, onCancel, onConfirm }: Props) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [entries, setEntries] = useState<Record<string, ManualTrackingEntry>>({});

    const entryFor = (orderId: string): ManualTrackingEntry => entries[orderId] ?? EMPTY;

    const setField = (orderId: string, field: keyof ManualTrackingEntry, value: string) => {
        setEntries(prev => ({
            ...prev,
            [orderId]: { ...(prev[orderId] ?? EMPTY), [field]: value },
        }));
    };

    // Orders whose link won't survive the trip. Confirm is blocked while
    // any is outstanding — the alternative is fulfilling the order and
    // silently discarding the link the operator pasted.
    const badLinks = useMemo(
        () => orders.filter(o => !linkIsUsable(entryFor(o.order_id).tracking_url)).map(o => o.order_id),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orders, entries],
    );

    const filledCount = useMemo(
        () => orders.filter(o => hasAnything(entryFor(o.order_id))).length,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orders, entries],
    );

    const confirm = () => {
        const out = new Map<string, ManualTrackingEntry>();
        for (const o of orders) {
            const e = entryFor(o.order_id);
            if (!hasAnything(e)) continue;
            out.set(o.order_id, {
                carrier: e.carrier.trim(),
                tracking_number: e.tracking_number.trim(),
                tracking_url: e.tracking_url.trim(),
            });
        }
        onConfirm(out);
    };

    return (
        <div className="modal-overlay" onClick={busy ? undefined : onCancel}>
            <div
                className={`modal-card dispatch-manual-fulfil-modal${expanded ? " dispatch-manual-fulfil-modal--wide" : ""}`}
                onClick={e => e.stopPropagation()}
            >
                <div className="modal-card-header">
                    <h2>
                        {orders.length === 1
                            ? t("manual_fulfil_title_one")
                            : t("manual_fulfil_title", { n: String(orders.length) })}
                    </h2>
                    <button className="modal-close" onClick={onCancel} disabled={busy} aria-label={t("cancel")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <p className="dispatch-manual-fulfil-intro">{t("manual_fulfil_body")}</p>

                    {/* The orders being closed, named. On a long selection
                        this is the operator's only chance to notice a row
                        they didn't mean to tick. */}
                    {!expanded && (
                        <p className="dispatch-manual-fulfil-numbers">
                            {orders.map(o => o.order_number).join(", ")}
                        </p>
                    )}

                    <button
                        type="button"
                        className="dispatch-manual-fulfil-toggle"
                        onClick={() => setExpanded(v => !v)}
                        aria-expanded={expanded}
                        disabled={busy}
                    >
                        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        {t("manual_fulfil_add_tracking")}
                        {!expanded && filledCount > 0 && (
                            <span className="dispatch-manual-fulfil-filled">
                                {t("manual_fulfil_filled_count", { n: String(filledCount) })}
                            </span>
                        )}
                    </button>

                    {expanded && (
                        <div className="dispatch-manual-fulfil-tracking">
                            <p className="dispatch-manual-fulfil-hint">{t("manual_fulfil_tracking_hint")}</p>
                            <div className="dispatch-manual-fulfil-grid">
                                <span className="dispatch-manual-fulfil-head">{t("col_order_number")}</span>
                                <span className="dispatch-manual-fulfil-head">{t("col_courier")}</span>
                                <span className="dispatch-manual-fulfil-head">{t("col_tracking_number")}</span>
                                <span className="dispatch-manual-fulfil-head">{t("manual_fulfil_tracking_link")}</span>
                                {orders.map(o => {
                                    const e = entryFor(o.order_id);
                                    const linkBad = !linkIsUsable(e.tracking_url);
                                    return (
                                        <div className="dispatch-manual-fulfil-row" key={o.order_id}>
                                            <span
                                                className="dispatch-manual-fulfil-order"
                                                title={o.order_number}
                                            >
                                                {o.order_number}
                                            </span>
                                            <input
                                                type="text"
                                                className="dispatch-manual-fulfil-input"
                                                value={e.carrier}
                                                disabled={busy}
                                                placeholder={t("manual_fulfil_carrier_placeholder")}
                                                aria-label={`${o.order_number} — ${t("col_courier")}`}
                                                onChange={ev => setField(o.order_id, "carrier", ev.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="dispatch-manual-fulfil-input"
                                                value={e.tracking_number}
                                                disabled={busy}
                                                placeholder={t("manual_fulfil_code_placeholder")}
                                                aria-label={`${o.order_number} — ${t("col_tracking_number")}`}
                                                onChange={ev => setField(o.order_id, "tracking_number", ev.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className={`dispatch-manual-fulfil-input${linkBad ? " dispatch-manual-fulfil-input--bad" : ""}`}
                                                value={e.tracking_url}
                                                disabled={busy}
                                                placeholder="https://…"
                                                aria-label={`${o.order_number} — ${t("manual_fulfil_tracking_link")}`}
                                                aria-invalid={linkBad}
                                                onChange={ev => setField(o.order_id, "tracking_url", ev.target.value)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            {badLinks.length > 0 && (
                                <p className="dispatch-manual-fulfil-error">{t("manual_fulfil_bad_link")}</p>
                            )}
                        </div>
                    )}

                    <div className="modal-confirm-buttons" style={{ marginTop: 18 }}>
                        <button
                            className="btn btn-ghost dispatch-modal-btn"
                            onClick={onCancel}
                            disabled={busy}
                        >
                            {t("cancel")}
                        </button>
                        <button
                            className="btn btn-success dispatch-modal-btn"
                            onClick={confirm}
                            disabled={busy || badLinks.length > 0}
                        >
                            <CheckCircle size={14} />
                            {busy ? t("submitting") : t("manual_fulfil_confirm")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManualFulfilModal;
